import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Budget, Category, Transaction } from '../types';
import { formatCurrency } from '../utils/dateUtils';

interface BudgetManagerProps {
  budgets: Budget[];
  categories: Category[];
  transactions: Transaction[];
  onAddBudget: (budget: Omit<Budget, 'id' | 'spent' | 'remaining'>) => void;
  onEditBudget: (budget: Budget) => void;
  onDeleteBudget: (budgetId: string) => void;
}

const BudgetManager: React.FC<BudgetManagerProps> = ({
  budgets,
  categories,
  transactions,
  onAddBudget,
  onEditBudget,
  onDeleteBudget,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);
  const [formData, setFormData] = useState({
    category: '',
    allocated: '',
    period: 'monthly' as 'monthly' | 'yearly',
  });

  const expenseCategories = categories.filter(cat => cat.type === 'expense');

  const budgetsWithSpending = useMemo(() => {
    return budgets.map(budget => {
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      
      const spent = transactions
        .filter(t => {
          const transactionDate = new Date(t.date);
          const isCorrectCategory = t.category === budget.category;
          const isExpense = t.type === 'expense';
          
          if (budget.period === 'monthly') {
            return isCorrectCategory && isExpense &&
                   transactionDate.getMonth() === currentMonth &&
                   transactionDate.getFullYear() === currentYear;
          } else {
            return isCorrectCategory && isExpense &&
                   transactionDate.getFullYear() === currentYear;
          }
        })
        .reduce((sum, t) => sum + t.amount, 0);

      return {
        ...budget,
        spent,
        remaining: budget.allocated - spent,
      };
    });
  }, [budgets, transactions]);

  const handleOpenDialog = (budget?: Budget) => {
    if (budget) {
      setEditingBudget(budget);
      setFormData({
        category: budget.category,
        allocated: budget.allocated.toString(),
        period: budget.period,
      });
    } else {
      setEditingBudget(null);
      setFormData({
        category: '',
        allocated: '',
        period: 'monthly',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBudget(null);
  };

  const handleSubmit = () => {
    if (!formData.category || !formData.allocated) return;

    const budgetData = {
      category: formData.category,
      allocated: parseFloat(formData.allocated),
      period: formData.period,
    };

    if (editingBudget) {
      onEditBudget({
        ...editingBudget,
        ...budgetData,
      });
    } else {
      onAddBudget(budgetData);
    }

    handleCloseDialog();
  };

  const getProgressColor = (spent: number, allocated: number) => {
    const percentage = (spent / allocated) * 100;
    if (percentage > 100) return 'error';
    if (percentage > 80) return 'warning';
    return 'success';
  };

  const getCategoryColor = (categoryName: string) => {
    const category = categories.find(cat => cat.name === categoryName);
    return category?.color || '#gray';
  };

  const totalBudget = budgetsWithSpending.reduce((sum, b) => sum + b.allocated, 0);
  const totalSpent = budgetsWithSpending.reduce((sum, b) => sum + b.spent, 0);
  const totalRemaining = totalBudget - totalSpent;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Gestion des budgets</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nouveau budget
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Résumé général
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary">Budget total</Typography>
            <Typography variant="h6">{formatCurrency(totalBudget)}</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary">Dépensé</Typography>
            <Typography variant="h6" color="error.main">{formatCurrency(totalSpent)}</Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 150 }}>
            <Typography variant="body2" color="text.secondary">Restant</Typography>
            <Typography variant="h6" color={totalRemaining >= 0 ? 'success.main' : 'error.main'}>
              {formatCurrency(totalRemaining)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {budgetsWithSpending.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Aucun budget configuré
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Créez votre premier budget pour commencer à suivre vos dépenses par catégorie
            </Typography>
            <Button variant="contained" onClick={() => handleOpenDialog()}>
              Créer un budget
            </Button>
          </Paper>
        ) : (
          budgetsWithSpending.map((budget) => {
            const progressPercentage = Math.min((budget.spent / budget.allocated) * 100, 100);
            const isOverBudget = budget.spent > budget.allocated;
            
            return (
              <Card key={budget.id} sx={{ mb: 1 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip
                          label={budget.category}
                          size="small"
                          sx={{
                            backgroundColor: getCategoryColor(budget.category),
                            color: 'white',
                          }}
                        />
                        <Chip
                          label={budget.period === 'monthly' ? 'Mensuel' : 'Annuel'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Typography variant="h6" color={isOverBudget ? 'error.main' : 'inherit'}>
                        {formatCurrency(budget.spent)} / {formatCurrency(budget.allocated)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Restant: {formatCurrency(budget.remaining)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(budget)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onDeleteBudget(budget.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <LinearProgress
                    variant="determinate"
                    value={progressPercentage}
                    color={getProgressColor(budget.spent, budget.allocated)}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {progressPercentage.toFixed(1)}% utilisé
                    {isOverBudget && (
                      <span style={{ color: 'red', fontWeight: 'bold' }}>
                        {' '}(Dépassement: {formatCurrency(budget.spent - budget.allocated)})
                      </span>
                    )}
                  </Typography>
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBudget ? 'Modifier le budget' : 'Nouveau budget'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Catégorie</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Catégorie"
              >
                {expenseCategories.map(category => (
                  <MenuItem key={category.id} value={category.name}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Montant alloué (€)"
              type="number"
              value={formData.allocated}
              onChange={(e) => setFormData({ ...formData, allocated: e.target.value })}
              inputProps={{ step: '0.01', min: '0' }}
            />

            <FormControl fullWidth>
              <InputLabel>Période</InputLabel>
              <Select
                value={formData.period}
                onChange={(e) => setFormData({ ...formData, period: e.target.value as 'monthly' | 'yearly' })}
                label="Période"
              >
                <MenuItem value="monthly">Mensuel</MenuItem>
                <MenuItem value="yearly">Annuel</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingBudget ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetManager;